import { Button, Heading, Timeline, Typography } from '@altify/components';
import { formatAssetAmount, useContent } from '@altify/utils';
import { useNavigate } from '@tanstack/react-router';
import ErrorImage from 'app/_authenticated/trade/assets/Error.svg';
import { useRef } from 'react';
import { Animated, Dimensions, View } from 'react-native';

import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import CurrencyIcon from 'app/_authenticated/trade/components/CurrencyIcon/CurrencyIcon';
import { submitOrderContent } from 'app/_authenticated/trade/components/SubmitOrderModal/SubmitOrderModal.content';
import { recurringBuySharedContent } from 'app/_authenticated/trade/recurring-buy/recurringBuyShared.content';
import { tradeSharedContent } from 'app/_authenticated/trade/tradeShared.content';

import * as loaderAnimationData from 'app/_authenticated/trade/assets/lottie/altify-loader-blue.json';
import * as successAnimationData from 'app/_authenticated/trade/assets/lottie/altify-loader-success-white.json';
import { DialogOrBottomSheet } from 'components/DialogOrBottomSheet/DialogOrBottomSheet';
import { useProduct } from 'hooks/products/useProducts';
import { content } from './RecurringBuyResumeModal.content';

const RecurringBuyResumeModal = ({
  schedule,
  fromAmount,
  nextExecution,
  fromProductCode,
  toProductCode,
  resumeComplete,
  resumeError,
  onClose,
  onCancelResume,
  onRetryResume,
}: {
  schedule: string;
  fromProductCode: string;
  toProductCode: string;
  fromAmount: number;
  nextExecution: string;
  resumeComplete?: boolean;
  resumeError?: boolean;
  onCancelResume: () => void;
  onRetryResume: () => void;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const { product: toProduct } = useProduct(toProductCode);
  const animation = useRef(new Animated.Value(0)).current;
  const localizedContent = useContent(
    content,
    submitOrderContent,
    recurringBuySharedContent,
    tradeSharedContent,
  );

  const screenWidth = Dimensions.get('window').width;
  const screenHeight = Dimensions.get('window').height;

  const animatedStyle = {
    backgroundColor: animation.interpolate({
      inputRange: [0, 1],
      outputRange: ['transparent', '#626AFF'],
    }),
    width: animation.interpolate({
      inputRange: [0, 1],
      outputRange: [0, screenWidth * 2],
    }),
    height: animation.interpolate({
      inputRange: [0, 1],
      outputRange: [0, screenHeight * 2],
    }),
    borderRadius: animation.interpolate({
      inputRange: [0, 1],
      outputRange: [0, screenWidth],
    }),
  };

  const createdStep = {
    title: localizedContent.recurringCreated,
    subtitle: schedule,
    state: 'complete',
  } as const;

  const nextBuyStep = {
    title: `${localizedContent.nextBuyOf} ${formatAssetAmount(
      fromAmount,
      fromProductCode,
      { showSpacer: true },
    )}`,
    subtitle: nextExecution,
    state: 'pending',
  } as const;

  const timelineSteps = [createdStep, nextBuyStep];

  const statusText = resumeComplete
    ? localizedContent.recurringBuyResumed
    : localizedContent.resumingRecurringBuy;

  return (
    <DialogOrBottomSheet
      dismissable={resumeComplete || resumeError}
      blurEnabled={true}
      onClose={() => onClose?.()}
      footerContent={
        <>
          {resumeError ? (
            <View className="w-full items-center px-4 pb-2">
              <Button
                size="large"
                color="primary"
                onPress={() => {
                  onRetryResume?.();
                }}
                text={localizedContent.retry}
                className="my-2 w-full"
              />
              <Button
                size="large"
                color="secondary"
                onPress={() => {
                  onCancelResume?.();
                }}
                text={localizedContent.cancelTrade}
                className="mt-1"
              />
            </View>
          ) : (
            resumeComplete && (
              <View className="w-full items-center px-4 pb-4">
                <Button
                  size="large"
                  color="white"
                  onPress={() => {
                    navigate({ to: '/products' });
                    onClose?.();
                  }}
                  text={localizedContent.exploreProducts}
                  className="mt-2"
                />
                <Button
                  color="secondary"
                  text={localizedContent.manageRecurringBuys}
                  onPress={() => {
                    navigate({ to: '/recurring-buys' });
                    onClose?.();
                  }}
                  className="mt-3"
                />
              </View>
            )
          )}
        </>
      }
      backgroundColor={resumeComplete ? '#626AFF' : '#0C1C2C'}>
      <View className="w-full flex flex-col items-center px-4 py-4">
        <View className="w-full items-center justify-center">
          {resumeError ? (
            <View className="mb-4 mt-6 items-center">
              <img src={ErrorImage} />
              <Heading level={5} className="mt-4 text-center">
                {localizedContent.genericActionError}
              </Heading>
            </View>
          ) : (
            <>
              <Animated.View
                style={[
                  { position: 'absolute', height: '100%' },
                  animatedStyle,
                ]}
              />
              <DotLottieReact
                data={
                  resumeComplete ? successAnimationData : loaderAnimationData
                }
                key={resumeComplete ? 'success' : 'loader'}
                autoplay={true}
                loop={!resumeComplete}
                style={{ width: '100%', height: 100 }}
              />
              <Heading level={4} className="mb-2">
                {statusText}
              </Heading>
              {!resumeComplete && (
                <Typography size="large" className="text-neutral-300">
                  {localizedContent.thisMayTakeAMoment}
                </Typography>
              )}
              <View className="mb-5" />
              <View>
                <CurrencyIcon
                  invertedColors={resumeComplete}
                  size={48}
                  code={toProductCode}
                  bottomRightIcon="RECURRING_BUY"
                  cornerIconBackground={resumeComplete ? '#626AFF' : '#0C1C2C'}
                />
              </View>
              <Typography size="large" className="mt-4">
                {localizedContent.youWillBeBuying}
              </Typography>

              <Heading level={2}>
                {!resumeComplete
                  ? toProduct?.name
                  : formatAssetAmount(fromAmount, fromProductCode, {
                      showSpacer: true,
                    })}
              </Heading>
              {resumeComplete && (
                <Timeline className="my-4" steps={timelineSteps} />
              )}
            </>
          )}
        </View>
      </View>
    </DialogOrBottomSheet>
  );
};

export default RecurringBuyResumeModal;
